import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { getEpisode, getSeriesPoints } from '../api';
import { DIRECTION_NOTE, day, direction, geoName, num, paragraphs, pct, share, stateLabel, stateNote, words } from '../format';
import DetailSkeleton from './DetailSkeleton';
import Flag from './Flag';
import Logo from './Logo';
import SeriesChart from './SeriesChart';

const DIMENSIONS = [
  ['momentum_score', 'momentum'],
  ['novelty_score', 'novelty'],
  ['strength_score', 'strength'],
  ['consistency_score', 'consistency'],
  ['pattern_score', 'pattern'],
  ['volume_score', 'volume'],
];

export default function EpisodeView() {
  const episodeId = Number(useParams().episodeId);
  /* Set by the tile that was clicked, and absent for every other way in - a
   * search hit, a company page, a pasted URL - where there is no list behind
   * this page to return to and the unfiltered grid is the honest answer. */
  const backTo = `/trends${useLocation().state?.list || ''}`;
  const [data, setData] = useState(null);
  const [points, setPoints] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setPoints([]);
    setError(null);
    getEpisode(episodeId).then(setData).catch(() => setError('Could not load this episode'));
  }, [episodeId]);

  useEffect(() => {
    const sid = data?.episode?.series_id;
    if (sid) getSeriesPoints(sid).then(setPoints).catch(() => setPoints([]));
  }, [data]);

  if (error) return <div className="tw-empty">{error}</div>;
  if (!data) return <DetailSkeleton />;

  const e = data.episode;
  const a = data.analysis;
  const s = data.scores;
  const dir = direction(a?.sentiment_score);

  return (
    <div className="tw-detail">
      <Link className="tw-back" to={backTo}>&larr; All trends</Link>

      <div className="tw-detail-head">
        <h1 className="tw-h1">
          <Logo symbol={e.eodhd_symbol} name={e.company} ticker={e.ticker} size={34} />
          <span className="sym">{e.ticker}</span>
          {e.trend_term}
        </h1>
        <div className="tw-crumbs">
          <Link className="tw-link" to={`/companies/${e.company_id}`}>{e.company}</Link>
          <span className="sep">/</span>
          <span className="tw-crumb-geo">
            <Flag geo={e.geo} />
            {geoName(e.geo)}
          </span>
          {e.brand && (
            <>
              <span className="sep">/</span>
              {e.brand}
            </>
          )}
          {e.revenue_share_pct ? (
            <>
              <span className="sep">/</span>
              {num(e.revenue_share_pct)}% of revenue
            </>
          ) : null}
        </div>
        <div className="tw-tags">
          <span className={`tw-tag ${e.is_open === false ? '' : 'good'}`}
                title={stateNote(e.state) || undefined}>
            {stateLabel(e.state)}
          </span>
          {e.archetype && <span className="tw-tag">{words(e.archetype)}</span>}
          {e.search_relatedness === 'low' && (
            <span className="tw-tag warn">ambiguous term</span>
          )}
        </div>
      </div>

      <div className="tw-block">
        <div className="tw-kv">
          <div>
            <div className="v">
              {e.weeks_open ?? 0}w <small>ago</small>
            </div>
            <div className="k">Broke out</div>
          </div>
          <div>
            <div className="v" style={{ fontSize: 16 }}>{day(e.started_on)}</div>
            <div className="k">Started</div>
          </div>
          <div>
            <div className={`v ${e.pct_change > 0 ? 'up' : e.pct_change < 0 ? 'down' : ''}`}>
              {pct(e.pct_change)}
            </div>
            <div className="k">Year on year</div>
          </div>
          <div>
            <div className="v">{num(e.break_z, 1)}</div>
            <div className="k">Break z-score</div>
          </div>
          {/* Absolute demand. The 0-100 index says how this term compares with
            * its own past and nothing about whether anyone is searching at all,
            * which is the first thing a reader wants to know. Only shown where
            * Ads returned a figure - it covers about four years against Trends'
            * five and omits small keywords entirely. */}
          {e.monthly_volume ? (
            <div>
              <div className="v">{Number(e.monthly_volume).toLocaleString()}</div>
              <div className="k">Est. searches a month</div>
            </div>
          ) : null}
          {a?.materiality_score !== null && a?.materiality_score !== undefined && (
            <div>
              <div className="v">{num(a.materiality_score)}</div>
              <div className="k">Materiality</div>
            </div>
          )}
          {/* Spelled out here in a way the tile has no room for. Materiality is
            * the stat immediately before it deliberately: the two are one
            * reading split in half, and read together they say how much this
            * matters and which way. */}
          {dir && (
            <div>
              <div className={`v ${dir.cls}`}>{dir.label}</div>
              <div className="k" title={DIRECTION_NOTE}>
                Direction &middot; {dir.word.toLowerCase()}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="tw-block">
        <h2 className="tw-block-title">Five year search interest</h2>
        <SeriesChart
          points={points}
          markers={e.started_on ? [{ time: e.started_on, text: 'Trend signal' }] : []}
        />
      </div>

      {a?.analysis && (
        <div className="tw-block">
          <h2 className="tw-block-title">What is happening</h2>
          {/* One column of paragraphs, filling a card the width of the chart
            * and the tables. The empty right half this started as was the 68ch
            * cap on .tw-prose, which .tw-explain lifts. */}
          <div className="tw-card tw-explain">
            {a.trend_driver && <p className="tw-prose lead">{a.trend_driver}</p>}
            {paragraphs(a.analysis).map((p, i) => (
              <p className="tw-prose" key={i}>{p}</p>
            ))}
            {a.model && (
              <div className="tw-note">
                {a.model} · written {day(a.created_at)} and carried forward while this
                trend stays open
              </div>
            )}
          </div>
        </div>
      )}

      {e.also_means && (
        <div className="tw-block">
          <h2 className="tw-block-title">What else this term can mean</h2>
          <div className="tw-prose">{e.also_means}</div>
        </div>
      )}

      {s && (
        <div className="tw-block">
          <h2 className="tw-block-title">
            Shape of the move{s.archetype_label ? `: ${s.archetype_label}` : ''}
          </h2>
          <div className="tw-table-wrap">
            <table className="tw-table">
              <tbody>
                {DIMENSIONS.filter(([key]) => s[key] !== null && s[key] !== undefined).map(
                  ([key, label]) => (
                    <tr key={key}>
                      <td className="lead" style={{ width: 130 }}>{label}</td>
                      <td className="tw-bar-cell">
                        <div className="tw-bar-track">
                          <div
                            className="tw-bar-fill"
                            style={{ width: `${Math.max(2, Math.min(100, s[key]))}%` }}
                          />
                        </div>
                      </td>
                      <td className="n lead" style={{ width: 56 }}>{num(s[key])}</td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.weeks?.length > 1 && (
        <div className="tw-block">
          <h2 className="tw-block-title">Week by week</h2>
          <div className="tw-table-wrap">
            <table className="tw-table">
              <thead>
                <tr>
                  <th>Week</th>
                  <th>State</th>
                  <th className="n">Value</th>
                  <th className="n">Holding</th>
                  <th className="n">z</th>
                  <th className="n">Slope t</th>
                </tr>
              </thead>
              <tbody>
                {data.weeks.map((w) => (
                  <tr key={w.run_id}>
                    <td className="lead">{day(w.week_end)}</td>
                    <td title={stateNote(w.state) || undefined}>{stateLabel(w.state)}</td>
                    <td className="n">{num(w.value)}</td>
                    <td className="n">{share(w.retention)}</td>
                    <td className="n">{num(w.robust_z, 1)}</td>
                    <td className="n">{num(w.slope_t, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {e.close_reason && (
        <div className="tw-block">
          <h2 className="tw-block-title">How it ended</h2>
          <div className="tw-prose">
            {e.close_detail || words(e.close_reason)} — closed {day(e.closed_on)}.
          </div>
        </div>
      )}
    </div>
  );
}
